'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface FormData {
  fullName: string;
  email: string;
  phone: string;
  collegeName: string;
  department: string;
  designation: string;
  numberOfStudents: string;
  preferredDate: string;
  message: string;
}

export default function RegistrationForm() {
  const [formData, setFormData] = useState<FormData>({
    fullName: '',
    email: '',
    phone: '',
    collegeName: '',
    department: '',
    designation: '',
    numberOfStudents: '',
    preferredDate: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');
    setErrorMessage('');

    try {
      const response = await fetch('/api/college-session-submit.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          fullName: formData.fullName,
          email: formData.email,
          phone: formData.phone,
          collegeName: formData.collegeName,
          department: formData.department,
          designation: formData.designation,
          numberOfStudents: formData.numberOfStudents,
          preferredDate: formData.preferredDate,
          message: formData.message
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSubmitStatus('success');
        setFormData({
          fullName: '',
          email: '',
          phone: '',
          collegeName: '',
          department: '',
          designation: '',
          numberOfStudents: '',
          preferredDate: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
        setErrorMessage(data.error || 'Something went wrong. Please try again.');
      }
    } catch (error) {
      setSubmitStatus('error');
      setErrorMessage('Failed to submit. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="registration-form" className="py-20 bg-gradient-to-br from-primary/5 via-accent/5 to-sky-light/5">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center space-x-2 bg-green-500/20 text-green-600 px-4 py-2 rounded-full mb-4 border-2 border-green-500/30">
            <Icon name="SparklesIcon" size={20} variant="solid" />
            <span className="text-sm font-bold uppercase tracking-wide">Book Your FREE Session</span>
          </div>
          
          <h2 className="text-4xl md:text-5xl font-headline font-bold text-foreground mb-4">
            Reserve Your Spot Today
          </h2>
          <p className="text-lg text-muted-foreground font-body">
            Fill in your college details and we'll get in touch to schedule the session
          </p>
        </div>

        <div className="bg-card rounded-2xl p-8 shadow-xl border border-border">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Personal Details */}
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="fullName" className="block text-sm font-semibold text-foreground mb-2">
                  Your Name *
                </label>
                <input
                  type="text"
                  id="fullName"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-surface border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-foreground"
                  placeholder="e.g., Dr. Priya Sharma"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-foreground mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-surface border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-foreground"
                  placeholder="your.email@college.edu"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="phone" className="block text-sm font-semibold text-foreground mb-2">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-surface border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-foreground"
                  placeholder="+91 98765 43210"
                />
              </div>

              <div>
                <label htmlFor="designation" className="block text-sm font-semibold text-foreground mb-2">
                  Your Designation *
                </label>
                <select
                  id="designation"
                  name="designation"
                  value={formData.designation}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-surface border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-foreground"
                >
                  <option value="">Select designation</option>
                  <option value="Training & Placement Officer">Training & Placement Officer</option>
                  <option value="Head of Department">Head of Department</option>
                  <option value="Dean">Dean</option>
                  <option value="Professor">Professor</option>
                  <option value="Assistant Professor">Assistant Professor</option>
                  <option value="Coordinator">Coordinator</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </div>

            {/* College Details */}
            <div>
              <label htmlFor="collegeName" className="block text-sm font-semibold text-foreground mb-2">
                College Name *
              </label>
              <input
                type="text"
                id="collegeName"
                name="collegeName"
                value={formData.collegeName}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 bg-surface border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-foreground"
                placeholder="e.g., RV College of Engineering"
              />
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="department" className="block text-sm font-semibold text-foreground mb-2">
                  Department *
                </label>
                <input
                  type="text"
                  id="department"
                  name="department"
                  value={formData.department}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-surface border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-foreground"
                  placeholder="e.g., Computer Science"
                />
              </div>

              <div>
                <label htmlFor="numberOfStudents" className="block text-sm font-semibold text-foreground mb-2">
                  Expected Attendees *
                </label>
                <select
                  id="numberOfStudents"
                  name="numberOfStudents"
                  value={formData.numberOfStudents}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-surface border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-foreground"
                >
                  <option value="">Select range</option>
                  <option value="50-100">50-100 students</option>
                  <option value="100-200">100-200 students</option>
                  <option value="200-500">200-500 students</option>
                  <option value="500+">500+ students</option>
                </select>
              </div>
            </div>

            <div>
              <label htmlFor="preferredDate" className="block text-sm font-semibold text-foreground mb-2">
                Preferred Date Range
              </label>
              <input
                type="text"
                id="preferredDate"
                name="preferredDate"
                value={formData.preferredDate}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-surface border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-foreground"
                placeholder="e.g., Last week of February or Early March"
              />
            </div>

            <div>
              <label htmlFor="message" className="block text-sm font-semibold text-foreground mb-2">
                Additional Information (Optional)
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows={4}
                className="w-full px-4 py-3 bg-surface border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-foreground"
                placeholder="Any specific requirements or questions..."
              />
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full px-8 py-4 bg-gradient-to-r from-primary to-accent text-primary-foreground rounded-lg font-headline font-semibold text-lg shadow-sky-md hover:shadow-sky-lg transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
            >
              {isSubmitting ? (
                <>
                  <Icon name="ArrowPathIcon" size={20} variant="outline" className="animate-spin" />
                  <span>Submitting...</span>
                </>
              ) : (
                <>
                  <span>Book FREE Session Now</span>
                  <Icon name="ArrowRightIcon" size={20} variant="solid" />
                </>
              )}
            </button>

            {/* Status Messages */}
            {submitStatus === 'success' && (
              <div className="p-4 bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800 rounded-lg flex items-start space-x-3">
                <Icon name="CheckCircleIcon" size={24} variant="solid" className="text-green-500 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-green-800 dark:text-green-200">Request Submitted Successfully!</p>
                  <p className="text-sm text-green-700 dark:text-green-300 mt-1">
                    Thank you for your interest. Our team will contact you within 24 hours to schedule the session.
                  </p>
                </div>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="p-4 bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start space-x-3">
                <Icon name="ExclamationCircleIcon" size={24} variant="solid" className="text-red-500 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-red-800 dark:text-red-200">Submission Failed</p>
                  <p className="text-sm text-red-700 dark:text-red-300 mt-1">{errorMessage}</p>
                </div>
              </div>
            )}
          </form>
        </div>

        {/* Contact Alternative */}
        <div className="mt-8 text-center">
          <p className="text-muted-foreground mb-4">
            Prefer to talk directly? Call us or send an email
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="tel:+918055579698"
              className="inline-flex items-center space-x-2 text-primary hover:text-primary/80 font-semibold transition-colors duration-300"
            >
              <Icon name="PhoneIcon" size={18} variant="solid" />
              <span>+91 80555 79698</span>
            </a>
            <span className="hidden sm:inline text-muted-foreground">|</span>
            <a
              href="mailto:info@vitiquest.com?subject=College%20Session%20Enquiry&body=Hello%20VitiQuest%20Team%2C%0D%0A%0D%0AI%20would%20like%20to%20book%20a%20FREE%20AI%20%26%20Job%20Awareness%20Session%20for%20our%20college.%0D%0A%0D%0ACollege%20Name%3A%20%0D%0ADepartment%3A%20%0D%0AMy%20Designation%3A%20%0D%0AExpected%20Students%3A%20%0D%0APreferred%20Date%3A%20%0D%0A%0D%0AThank%20you!"
              className="inline-flex items-center space-x-2 text-primary hover:text-primary/80 font-semibold transition-colors duration-300"
            >
              <Icon name="EnvelopeIcon" size={18} variant="solid" />
              <span>info@vitiquest.com</span>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
